import React, { useMemo, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import Project from '../../../components/Inputs/Project';
import Customer from '../../../components/Inputs/Customer';
import Enduser from '../../../components/Inputs/Enduser';
import Input from '../../../common/Form/Input';
import TextArea from '../../../common/Form/TextArea';
import FileUpload from '../../../common/Form/FileUpload';
import { actualValueForDropdown, devLog, ScogoID } from '../../../utils/common';
import RenderRemarksInput from '../../../components/Inputs/RenderRemarksInput';
import { convertIntoRupee } from '../../../utils/utils';
import Site from '../../../components/Inputs/Site';
import Address from '../../../components/Inputs/Address';
import RenderContact from '../../../components/Inputs/Contact';
import { MilestoneType } from '../TicketController';
import Branch from '../../../components/Inputs/Branch';
import MultiSelectDateTimePicker from '../../../common/Form/MultiSelectDateTimePicker';
import IconToolTip from '../../../common/IconToolTip';
import { useDispatch, useSelector } from 'react-redux';
import { getInventoryLocations } from '../../../actions/inventory';

const Basic = ({ setProjectMilestones, projectMilestones }) => {
    const formContext = useFormContext();
    const watch = formContext.watch;
    const selectedProject = watch('project');
    const selectedOrganization = watch('organization');
    const dispatch = useDispatch();
    const [showSiteExistsMessage, setShowSiteExistsMessage] = useState(false);
    const [hasCheckedSite, setHasCheckedSite] = useState(false);

    const findRma = useMemo(() => {
        return projectMilestones && projectMilestones.find((mile) => mile.type == MilestoneType.RMA);
    }, [projectMilestones]);
    const siteAlreadyExists = useSelector(state => state.inventory.siteAlreadyExists);
    const findRemoteSupport = useMemo(() => {
        return projectMilestones && projectMilestones.find((mile) => mile.type == MilestoneType.REMOTE_SUPPORT);
    }, [projectMilestones]);

    const isOnlyLogistics = useMemo(() => {
        if (projectMilestones.length > 0) {
            return projectMilestones.every(elem => elem.type === MilestoneType.LOGISTICS);
        }
        return false;
    }, [projectMilestones]);

    const handleProjectChange = (project) => {
        if (!project?.milestones) return;
        setProjectMilestones(project.milestones);
        project.milestones.forEach((milestone, index) => {
            formContext.setValue(`milestone-${milestone.type}-${index}:SP_SERVICE`, convertIntoRupee(project.payments.SP_SERVICE));
            formContext.setValue(`milestone-${milestone.type}-${index}:PSP_INCENTIVE`, convertIntoRupee(project.payments.PSP_INCENTIVE));
            formContext.setValue(`milestone-${milestone.type}-${index}:CLUSTER_INCENTIVE`, convertIntoRupee(project.payments.CLUSTER_INCENTIVE));
            formContext.setValue(`milestone-${milestone.type}-${index}:WAREHOUSE_OWNER_INCENTIVE`, convertIntoRupee(project.payments.WAREHOUSE_OWNER_INCENTIVE));
        });

        const isRma = project.milestones.find(milestone => milestone.type === MilestoneType.RMA);
        if (isRma) {
            formContext.setValue('location_id', null);
        }
    }

    const prefillObj = useMemo(() => {
        return selectedProject?.organization ? { key: selectedProject?.organization.id, label: selectedProject?.organization.name, value: selectedProject?.organization.id } : {};
    }, [selectedProject]);

    const isAddNewSite = actualValueForDropdown(watch('location_id')) === 'add-new-site';
    const org_id = actualValueForDropdown(selectedOrganization);

    // useEffect to handle siteAlreadyExists state changes
    useEffect(() => {
        if (hasCheckedSite && siteAlreadyExists) {
            setShowSiteExistsMessage(true);
        } else {
            setShowSiteExistsMessage(false);
        }
    }, [siteAlreadyExists, hasCheckedSite]);

    const getSites = ({ limit = 100, page = 1, siteId = '' }) => {
        setHasCheckedSite(true);
        dispatch(getInventoryLocations({ site_id: siteId, limit, page, dropdown: true, type: 'SITE', payload: { org_id } }));
    };

    const isExternalTicketIdRequired = useMemo(() => {
        if (!selectedProject) return false;
        devLog('selectedProject', selectedProject);
        if (selectedProject.default_helpdesk_id === ScogoID) return selectedProject.milestones.find(milestone => milestone.type === MilestoneType.RMA);
        return false;
    }, [selectedProject]);

    const isExecutionDateRequired = useMemo(() => {
        if (!selectedProject) return false;
        devLog('selectedProject', selectedProject);
        if (selectedProject.category && selectedProject.default_helpdesk_id === ScogoID && selectedProject.category.toUpperCase() === 'HELPDESK') return true;
        return false;
    }, [selectedProject]);

    const memoizedSelectedEnduser = useMemo(() => {
        return selectedProject && selectedProject.end_user?.id ? [selectedProject.end_user.id] : undefined;
    }, [selectedProject]);

    return (
        <motion.div
            className='rounded-lg bg-gray-100 px-6 py-3 transition-transform transform hover:scale-105'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <p className='text-lg font-semibold text-scogoprimary mb-2 border-b-2 border-scogoprimary pb-2'>Basic Details</p>
            <motion.div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
                <motion.div transition={{ delay: 0.1 }}>
                    <Project name='project' label='Project' required org_check_required={false} onChange={handleProjectChange} />
                </motion.div>
                <motion.div transition={{ delay: 0.2 }}>
                    <Customer disabled name='organization' label='Customer' prefillObj={prefillObj} required />
                </motion.div>
                <Enduser add_new name='enduser' label='Enduser' org_id={actualValueForDropdown(selectedOrganization)} prefill={memoizedSelectedEnduser} />
                <motion.div transition={{ delay: 0.4 }}>
                    <Input name='title' label='Title' placeholder='Enter ticket title' className='rounded-mm focus:outline-none focus:ring-2 focus:ring-scogoprimary' />
                </motion.div>
                <motion.div transition={{ delay: 0.5 }}>
                    <TextArea name='description' label='Description' placeholder='Provide a detailed description' className='rounded-md focus:outline-none focus:ring-2 focus:ring-scogoprimary' />
                </motion.div>
                <motion.div transition={{ delay: 0.6 }}>
                    <RenderRemarksInput name='issue_category' label='Issue Category' category='ISSUE_CATEGORY' project_id={actualValueForDropdown(selectedProject)} org_id={actualValueForDropdown(selectedOrganization)} required={false} />
                </motion.div>
                <motion.div transition={{ delay: 0.6 }}>
                    {/* <MultiSelectDatePicker className='w-full' name={`execution_dates`} label="Execution Date" minDate={new Date()} /> */}
                    <MultiSelectDateTimePicker required={isExecutionDateRequired} className='w-full' name={`execution_dates`} label="Execution Date" minDate={new Date()} />
                </motion.div>

                <motion.div transition={{ delay: 0.6 }}>
                    <Site onChange={(event, { data }) => {
                        if (data?.location?.address) {
                            formContext.setValue('location_id:selected_site_address', data.location.address);
                        } else {
                            formContext.setValue('location_id:selected_site_address', null);
                        }
                    }} name={`location_id`} label={isOnlyLogistics ? "Pickup Details" : "Site Details"} addNew={true} org_id={actualValueForDropdown(selectedOrganization)} disabled={findRma} required={!findRemoteSupport} />
                </motion.div>

                {watch('location_id:selected_site_address') ? <motion.div className='my-auto flex item-center overflow-hidden' transition={{ delay: 0.6 }}>
                    <IconToolTip title={watch('location_id:selected_site_address')}>
                        <div className='cursor-pointer shadow-sm h-full break-all overflow-hidden flex rounded-md items-center justify-center px-2 ml-2 border-gray-300 border-1 text-gray-600 font-normal bg-white max-h-max py-1'>
                            <span onClick={() => {
                                window.open(`https://maps.google.com?q=${watch('location_id:selected_site_address')}`, '_blank')
                            }} className='line-clamp-3 break-all hover:underline'>{watch('location_id:selected_site_address')}</span>
                        </div>
                    </IconToolTip>
                </motion.div> : null}

                <motion.div transition={{ delay: 0.4 }}>
                    <Input required={isExternalTicketIdRequired} name='external_ticket_id' label='External Ticket Id' className='rounded-mm focus:outline-none focus:ring-2 focus:ring-scogoprimary' />
                </motion.div>

                {isAddNewSite && <Input
                    required
                    name={`location_id:site-external-id`}
                    label="Site ID"
                    onBlur={(e) => {
                        // Call your function here when user finishes writing site ID
                        const siteId = e.target.value;
                        if (siteId && siteId.trim()) {
                            // Add your logic here
                            console.log('Site ID completed:', siteId);
                            getSites({ limit: 100, page: 1, siteId: siteId });
                        }
                    }}
                />}
                {isAddNewSite && showSiteExistsMessage && (
                    <div className="col-span-full">
                        <p className="text-red-500 text-sm font-medium mt-1">
                            This site already exists
                        </p>
                    </div>
                )}
                {isAddNewSite && <Branch add_new org_id={actualValueForDropdown(selectedOrganization)} name={`location_id:site-branch-name`} label="Branch Name" showInputIfEmpty />}
                {isAddNewSite && <Address required name={`location_id:site-address`} label="Address" />}
                {isAddNewSite && <Input label='Region' name='location_id:region' />}
                {isAddNewSite && <Input label='Zone' name='location_id:zone' />}
                {isAddNewSite && <RenderContact name={`location_id:site-contact`} label="Contact" org_id={actualValueForDropdown(selectedOrganization)} />}

                {selectedProject?.custom_ticket_fields?.map((field) => {
                    if (field.type === 'FILE') {
                        return <FileUpload key={field.key} name={`custom_ticket_fields:${field.key}`} label={field.label} required={field.is_required} isMulti={true} appendFiles={true} />;
                    }
                    return <Input key={field.key} name={`custom_ticket_fields:${field.key}`} label={field.label} required={field.is_required} />;
                })}

                <RenderContact org_id={actualValueForDropdown(selectedOrganization)} name='ticket_contact' label='Ticket Contact' className='w-full' />
            </motion.div>
        </motion.div>
    );
};

export default Basic;
